import { Component, Inject, OnDestroy, OnInit } from '@angular/core';
import {
  MAT_LEGACY_DIALOG_DATA as MAT_DIALOG_DATA,
  MatLegacyDialogRef as MatDialogRef,
} from '@angular/material/legacy-dialog';
import { MatLegacySnackBar as MatSnackBar } from '@angular/material/legacy-snack-bar';
import { Subject } from 'rxjs';
import { finalize, takeUntil } from 'rxjs/operators';

// INTERFACES
import { SalesOrderSourcePlatform } from '@app/core/sales/store/sales-order.model';

import {
  ISalesOrder,
  EOrderPullUpdatesErrors,
  EPullUpdateStatus,
} from '../../../order/interfaces/order.interface';
import { SnackBarConfig } from '../../../shared/configs/snackbar.config';
import { OrderApiService } from '../../../shared/services/api/order/order-api.service';
import { trackById } from '../../../shared/util/track-by.util';
import { EAppAccountType } from '../../../system-setting/constants/app-integration-account.enum';
// SERVICES
// UTILS

interface IOrderData extends ISalesOrder {
  pullingStatus ?:string;
  errors ?:any;
}

@Component({
  selector: 'lum-order-shipping-updates',
  templateUrl: './order-shipping-updates.component.html',
  styleUrls: ['./order-shipping-updates.component.scss'],
})
export class OrderShippingUpdatesComponent implements OnInit, OnDestroy {
  salesOrders :IOrderData[] = [];
  filteredSalesOrders :IOrderData[] = [];
  loading :boolean = false;
  orderPullUpdatesErrors = EOrderPullUpdatesErrors;
  pullingStatus = EPullUpdateStatus;
  hasUpdates :boolean = false;

  trackById = trackById;

  private onDestroy$ = new Subject();

  constructor(
    private dialogRef :MatDialogRef<OrderShippingUpdatesComponent>,
    @Inject(MAT_DIALOG_DATA)
    public data :{
      title :string;
      salesOrders :ISalesOrder[];
      featureFlags :any;
      isSelectAllRows :boolean;
      remainingSelectableCount :number;
    },
    private orderApiService :OrderApiService,
    private snackBar :MatSnackBar,
  ) {}

  ngOnInit() :void {
    this.initSalesOrders();
  }

  ngOnDestroy() :void {
    this.onDestroy$.next(null);
    this.onDestroy$.complete();
  }

  onClose() :void {
    this.dialogRef.close({
      isSelectAllRows: false,
      hasUpdates: this.hasUpdates,
    });
    this.data.salesOrders = [];
  }

  onGetOrderUpdates() :void {
    this.loading = true;
    if (this.data.isSelectAllRows) {
      this.dialogRef.close({
        isSelectAllRows: true,
        hasUpdates: this.hasUpdates,
      });

      return;
    }
    this.salesOrders.forEach((order :IOrderData) => {
      order.pullingStatus = EPullUpdateStatus.GETTING_UPDATES;

      this.orderApiService
        .getOrderUpdates(order.id)
        .pipe(
          takeUntil(this.onDestroy$),
          finalize(() => {
            this.loading = false;
          }),
        )
        .subscribe({
          next: (response) => {
            order.pullingStatus = EPullUpdateStatus.UPDATED;
            if (this.finishedPullingUpdates) {
              this.snackBar.open('Pulling updates finished.', 'Close', {
                duration: SnackBarConfig.DEFAULT.duration,
              });
              this.hasUpdates = true;
            }
          },
          error: (err) => {
            order.pullingStatus = EPullUpdateStatus.FAILED;
            if (this.finishedPullingUpdates) {
              this.snackBar.open(
                err?.error ? err?.error : 'Failed to pull updates.',
                'Close',
                {
                  duration: SnackBarConfig.DEFAULT.duration,
                },
              );
            }
          },
        });
    });
  }

  private initSalesOrders() :void {
    this.data.salesOrders.forEach((order :IOrderData) => {
      const { source } = order;
      const shipstationOrderId = order.shipstationOrderId;
      const isPosted = order.isPosted ?? order.is_posted;
      const destinationReferenceIdentifier =
        order.destinationReferenceIdentifier ??
        order.destination_reference_identifier;

      if (
        isPosted &&
        (shipstationOrderId ||
          destinationReferenceIdentifier ||
          source !== SalesOrderSourcePlatform.EDI)
      ) {
        this.salesOrders.push(order);
      } else {
        this.filteredSalesOrders.push({
          ...order,
          ...{
            errors: {
              isPosted: !isPosted,
            },
          },
        });
      }
    });
  }

  get finishedPullingUpdates() :boolean {
    return (
      this.salesOrders.filter(
        (order :IOrderData) =>
          order.pullingStatus === EPullUpdateStatus.GETTING_UPDATES,
      ).length === 0
    );
  }
}
